import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 * Supports infinite scroll (via PaginatedList) and load-more button mode.
 */
export default class ResultsList extends PaginatedList {
  /** @type {boolean} */
  #isLoadMoreMode = false;

  /** @type {boolean} */
  #isLoadingMore = false;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #loadMoreAspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    this.#isLoadMoreMode = this.getAttribute('pagination-type') === 'load_more';

    if (this.#isLoadMoreMode) {
      this.#initLoadMore();
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    if (this.#isLoadMoreMode) {
      this.removeEventListener('click', this.#handleLoadMoreClick);
    }
  }

  /**
   * Initializes load-more mode: prefetches next page and binds button click.
   */
  #initLoadMore() {
    // MP: Bind via event delegation on the results-list element itself, which
    // persists across section morphs (applying a filter/sort re-renders the grid
    // in place). Previously the listener was bound to the button node, which the
    // morph replaced — silently breaking "Load More" after any filter change. This
    // was most visible on mobile, where filtering through the drawer is the common
    // flow. Page state is read fresh from the DOM on every click (see
    // #getNextPageInfo), so no internal counter can go stale when the product set
    // changes after filtering.
    // FIX: 2026-06-16 by MeshPlanet — Load More stops working after filtering (mobile)
    this.addEventListener('click', this.#handleLoadMoreClick);

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#loadMoreAspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }

    // Prefetch the next page for faster load
    this.#prefetchNextPage();
  }

  /**
   * Delegated click handler — runs the load only when the Load More button is clicked.
   * @param {Event} event
   */
  #handleLoadMoreClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[ref="loadMoreButton"]');
    if (!button || !this.contains(button)) return;

    this.#handleLoadMore();
  };

  /**
   * Gets the next page info, reading the current/last page fresh from the DOM each
   * call so the page counter can never go stale after a filter/sort re-render swaps
   * the product set.
   * @returns {{ page: number, url: URL } | null}
   */
  #getNextPageInfo() {
    const { grid } = this.refs;
    // MP: Take the last actual product card (not `:last-of-type`, which would miss the
    // last card when a collection banner <li> is the final grid child).
    const cards = grid?.querySelectorAll('[ref="cards[]"]');
    const lastCard = cards && cards.length ? cards[cards.length - 1] : null;
    const currentPage = lastCard instanceof HTMLElement ? Number(lastCard.dataset.page) : 1;
    const lastPage = grid ? Number(grid.dataset.lastPage) : 1;
    const nextPage = currentPage + 1;

    if (!lastPage || nextPage > lastPage) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage.toString());
    url.hash = '';

    return { page: nextPage, url };
  }

  /**
   * Prefetches the next page HTML via section renderer.
   */
  async #prefetchNextPage() {
    const nextPageInfo = this.#getNextPageInfo();
    if (!nextPageInfo) return;

    // Only prefetch if not already cached in PaginatedList's pages map
    if (this.pages.has(nextPageInfo.page)) return;

    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, nextPageInfo.url);
    this.pages.set(nextPageInfo.page, pageContent);
  }

  /**
   * Handles the load more button click.
   */
  #handleLoadMore = async () => {
    if (this.#isLoadingMore) return;

    const { grid, loadMoreButton, loadMoreContainer } = this.refs;
    if (!grid || !loadMoreButton) return;

    const nextPageInfo = this.#getNextPageInfo();
    if (!nextPageInfo) return;

    this.#isLoadingMore = true;
    loadMoreButton.disabled = true;

    // Fetch if not already cached
    if (!this.pages.has(nextPageInfo.page)) {
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, nextPageInfo.url);
      this.pages.set(nextPageInfo.page, pageContent);
    }

    const pageHTML = this.pages.get(nextPageInfo.page);
    if (!pageHTML) {
      this.#isLoadingMore = false;
      loadMoreButton.disabled = false;
      return;
    }

    // Parse and extract grid items from the fetched page
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) {
      this.#isLoadingMore = false;
      loadMoreButton.disabled = false;
      return;
    }

    const newItems = gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    if (newItems.length > 0) {
      grid.append(...newItems);
      this.#loadMoreAspectRatioHelper?.processNewElements();
      history.pushState('', '', nextPageInfo.url.toString());
      // MP: The page counter is derived from the DOM (last card's data-page),
      // so there is no internal state to advance here.
    }

    this.#isLoadingMore = false;
    loadMoreButton.disabled = false;

    // Check if there are more pages; hide button if not
    const followingPageInfo = this.#getNextPageInfo();
    if (!followingPageInfo) {
      if (loadMoreContainer) {
        loadMoreContainer.style.display = 'none';
      } else {
        loadMoreButton.style.display = 'none';
      }
    } else {
      // Prefetch next page for faster subsequent loads
      requestIdleCallback(() => {
        this.#prefetchNextPage();
      });
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
